/**
 * Beta feedback — an easy report path that works with today's
 * infrastructure: the report (plus the local friction-signal snapshot the
 * user can see before sending) is copied to the clipboard and offered as a
 * pre-filled email. No hidden collection, no new backend surface.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MessageSquarePlus, Copy, Mail } from "lucide-react";
import { betaSignalsSnapshot } from "../../lib/betaTelemetry";

const CATEGORIES = [
  { value: "cannot-complete", label: "I can't complete a workflow" },
  { value: "wrong-data", label: "The data shown looks wrong" },
  { value: "error", label: "Something failed / crashed" },
  { value: "confusing", label: "This is confusing" },
  { value: "idea", label: "Suggestion" },
];

const BetaFeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("cannot-complete");
  const [text, setText] = useState("");
  const [includeSignals, setIncludeSignals] = useState(true);

  const buildReport = () => {
    const lines = [
      `OMNΛS beta feedback — ${new Date().toISOString()}`,
      `Category: ${CATEGORIES.find((c) => c.value === category)?.label || category}`,
      `Route: ${window.location.pathname}`,
      "",
      text.trim(),
    ];
    if (includeSignals) {
      const snapshot = betaSignalsSnapshot();
      lines.push("", "--- Recent UI signals (local, last 15) ---", snapshot || "(none recorded)");
    }
    return lines.join("\n");
  };

  const copyReport = async () => {
    if (!text.trim()) {
      toast.error("Describe what happened first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(buildReport());
      toast.success("Report copied — paste it to the OMNΛS team channel or email.");
      setOpen(false);
      setText("");
    } catch {
      toast.error("Could not access the clipboard. Use the email option instead.");
    }
  };

  const emailReport = () => {
    if (!text.trim()) {
      toast.error("Describe what happened first.");
      return;
    }
    const subject = encodeURIComponent(`OMNΛS beta feedback: ${category}`);
    const body = encodeURIComponent(buildReport());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setOpen(false);
    setText("");
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 text-xs"
        onClick={() => setOpen(true)}
        title="Report beta friction"
      >
        <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />
        Beta feedback
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Beta feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">What kind of problem?</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">What happened, and what were you trying to do?</Label>
              <Textarea
                className="mt-1"
                rows={4}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. I approved the switchgear award but couldn't tell whether anything happened next."
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeSignals}
                onChange={(e) => setIncludeSignals(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Attach the recent local UI signal log (failed requests, disabled features —
                shown in the report before you send it). Nothing is sent automatically.
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={copyReport}>
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                Copy report
              </Button>
              <Button size="sm" onClick={emailReport}>
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                Email report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BetaFeedbackButton;
